import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface FavoriteWithActivity {
  id: string;
  user_id: string;
  activity_id: string;
  created_at: string;
  activity: {
    id: string;
    title_ar: string;
    title_en: string | null;
    price_kwd: number;
    gallery_urls: string[] | null;
  } | null;
}

export function useFavorites() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['favorites', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('favorites')
        .select(`
          *,
          activity:activities(id, title_ar, title_en, price_kwd, gallery_urls)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as FavoriteWithActivity[];
    },
    enabled: !!user,
  });
}

export function useAddFavorite() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (activityId: string) => {
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('favorites')
        .insert({ user_id: user.id, activity_id: activityId });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
    },
  });
}

export function useRemoveFavorite() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (activityId: string) => {
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('favorites')
        .delete()
        .eq('user_id', user.id)
        .eq('activity_id', activityId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
    },
  });
}

export function useIsFavorite(activityId: string) {
  const { data: favorites } = useFavorites();
  return favorites?.some(f => f.activity_id === activityId) ?? false;
}
